import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TableCell, TableRow, Typography, Chip } from "@mui/material";

import TruncateText from "../../../components/TruncateText";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import Tabs from "../../../components/Tabs";
import messages from "../../../assets/locale/messages";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import noImporterImg from "../../../assets/images/placeholders/noImportersPlaceholderImg.svg";
import {
  COMPANY_VERIFICATION_STATUS,
  CHIPS_CLASSES,
  BUSINESS_VERIFICATION_STATUS_LOCALE_KEYS
} from "../../../utils/Constants";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  areAllParamsEmpty,
  formatDate
} from "../../../utils/Helpers";
import { getVerificationsRequestsListRequest } from "../../../store/BusinessVerification/actions";
import {
  businessTypesLookupRequest,
  businessVerificationStatusLookupRequest,
  businessVerificationTypesLookupRequest,
  countriesLookupRequest
} from "../../../store/Lookups/actions";
import { showHideLoader } from "../../../store/Loader/actions";
import DateRangeFilter from "../../../components/Filters/DateRangeFilter";
import "./VerificationRequestsList.scss";

const VerificationRequestsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { businessVerification, shared } = messages[lang];

  const requestsTabsValues = {
    pending: 1,
    accepted: 2,
    rejected: 3
  };

  const requestsList = useSelector(
    (state) => state.businessVerification.verificationRequests.list
  );
  const requestsMeta = useSelector(
    (state) => state.businessVerification.verificationRequests.meta
  );
  const countriesLookup = useSelector(
    (state) => state.lookups.countries.list
  );
  const verificationTypesLookup = useSelector(
    (state) => state.lookups.businessVerificationTypes.list
  );
  const businessTypesLookup = useSelector(
    (state) => state.lookups.businessTypes.list
  );
  const verificationStatusLookup = useSelector(
    (state) => state.lookups.businessVerificationStatus.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [tabStatusValue, setTabStatusValue] = useState([
    requestsTabsValues.pending
  ]);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [businessTypeFilterValue, setBusinessTypeFilterValue] =
    useState([]);
  const [countryFilterValue, setCountryFilterValue] = useState([]);
  const [
    verificationMethodFilterValue,
    setVerificationMethodFilterValue
  ] = useState([]);
  const [statusFilterValue, setStatusFilterValue] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [dateRangeFilterValue, setDateRangeFilterValue] = useState({
    from: null,
    to: null
  });
  const [paramsObject, setParamsObject] = useState({
    business_type_ids: [],
    country_ids: [],
    verification_type_Ids: [],
    status_ids: [],
    date_range_from: null,
    date_range_to: null
  });

  useEffect(() => {
    dispatch(countriesLookupRequest());
    dispatch(businessVerificationTypesLookupRequest());
    dispatch(businessTypesLookupRequest());
    dispatch(businessVerificationStatusLookupRequest());
  }, []);

  const getRequestsList = () => {
    dispatch(
      getVerificationsRequestsListRequest({
        params: {
          page,
          search_value: searchValue,
          tab_id: tabStatusValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    dispatch(showHideLoader(true));
    let tabStatusValue = null;
    switch (tabValue) {
      case 0:
        tabStatusValue = requestsTabsValues.pending;
        break;
      case 1:
        tabStatusValue = requestsTabsValues.accepted;
        break;
      case 2:
        tabStatusValue = requestsTabsValues.rejected;
        break;
      default:
        break;
    }
    setClearFilters(true);
    setSearchValue("");
    setPage(1);
    setTabStatusValue([tabStatusValue]);
  }, [tabValue]);

  useEffect(() => {
    if (clearFilters) {
      setBusinessTypeFilterValue([]);
      setCountryFilterValue([]);
      setVerificationMethodFilterValue([]);
      setStatusFilterValue([]);
      setClearFilters(false);
      setDateRangeFilterValue({ from: null, to: null });
      setParamsObject({
        business_type_ids: [],
        country_ids: [],
        verification_type_Ids: [],
        status_ids: [],
        date_range_from: null,
        date_range_to: null
      });
    }
  }, [clearFilters]);

  useEffect(() => {
    getRequestsList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : getRequestsList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        business_type_ids: businessTypeFilterValue,
        country_ids: countryFilterValue,
        verification_type_Ids: verificationMethodFilterValue,
        status_ids: statusFilterValue,
        date_range_from: dateRangeFilterValue.from || null,
        date_range_to: dateRangeFilterValue.to || null
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  useEffect(() => {
    setParamsObject({
      business_type_ids: [],
      country_ids: [],
      verification_type_Ids: [],
      status_ids: []
    });
  }, [tabStatusValue]);

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="number-range-filter-container border-bottom py-3 pb-4 px-md-5 px-3">
        <DateRangeFilter
          name="Date"
          label={shared.filtersDrawerLabels.dateRange}
          onRequestFiltering={setDateRangeFilterValue}
          clearFilters={clearFilters}
          collabsible
          selectedDateFrom={dateRangeFilterValue?.from}
          selectedDateTo={dateRangeFilterValue?.to}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={countriesLookup}
          name="countries"
          label={businessVerification.list.filters.country}
          searchPlaceholder={
            businessVerification.list.filters.searchCountry
          }
          selectedOptionsIds={countryFilterValue}
          setSelectedOptionsIds={setCountryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={businessTypesLookup}
          name="business-type"
          label={businessVerification.list.filters.businessType}
          selectedOptionsIds={businessTypeFilterValue}
          setSelectedOptionsIds={setBusinessTypeFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>

      <div
        className={`py-3 px-md-5 px-3 ${tabValue === 0 && "border-bottom"}`}>
        <ListFilter
          list={verificationTypesLookup}
          name="verification-type"
          label={businessVerification.list.filters.verificationMethod}
          selectedOptionsIds={verificationMethodFilterValue}
          setSelectedOptionsIds={setVerificationMethodFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>
      {tabValue === 0 && (
        <div className="py-3 px-md-5 px-3">
          <ListFilter
            list={verificationStatusLookup}
            name="status"
            label={businessVerification.list.filters.status}
            searchPlaceholder={
              businessVerification.list.filters.searchStatus
            }
            selectedOptionsIds={statusFilterValue}
            setSelectedOptionsIds={setStatusFilterValue}
            clearFilters={clearFilters}
            collabsible
            showSearchBar
            filterModalOpen={filterModalOpen}
          />
        </div>
      )}
    </div>
  );

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const chipsStatusClass = (status) => {
    let chipClass = "";
    switch (status) {
      case COMPANY_VERIFICATION_STATUS.awaitingRequiredDocuments:
        chipClass = CHIPS_CLASSES.blue;
        break;
      case COMPANY_VERIFICATION_STATUS.pendingDocumentsUpload:
        chipClass = CHIPS_CLASSES.pink;
        break;
      case COMPANY_VERIFICATION_STATUS.documentsUploaded:
        chipClass = CHIPS_CLASSES.darkBlue;
        break;
      case COMPANY_VERIFICATION_STATUS.pendingPayment:
        chipClass = CHIPS_CLASSES.purple;
        break;
      case COMPANY_VERIFICATION_STATUS.docsNeedUpdates:
        chipClass = CHIPS_CLASSES.darkYellow;
        break;
      case COMPANY_VERIFICATION_STATUS.pendingFinalApproval:
        chipClass = CHIPS_CLASSES.orange;
        break;
      default:
        break;
    }
    return chipClass;
  };

  const renderRequestTableRow = ({
    Id,
    CompanyName,
    CountryName,
    BusinessTypeName,
    BusinessVerificationTypeName,
    RequestStatusId,
    LogoImage,
    BusinessVerificationTypeId,
    RequestDate
  }) => (
    <TableRow
      key={`request-row-${Id}`}
      className="pointer"
      onClick={() => {
        navigate(
          ROUTE_PATHS.verificationRequestDetails
            .replace(":type", BusinessVerificationTypeId)
            .replace(":id", Id)
        );
      }}>
      <TableCell style={{ width: `${tabValue !== 0 && "20%"}` }}>
        <div className="d-flex align-items-center gap-3 w-auto">
          <img
            src={LogoImage || noRequestImg}
            onError={onImageError}
            alt={"company-logo"}
            className="company-img faint-grey-border border-radius-8"
          />
          <TruncateText length={13} text={CompanyName || "__"} />
        </div>
      </TableCell>
      <TableCell style={{ width: `${tabValue !== 0 && "20%"}` }}>
        {(RequestDate && formatDate(RequestDate)) || "__"}
      </TableCell>
      <TableCell style={{ width: `${tabValue !== 0 && "20%"}` }}>
        <TruncateText text={CountryName || "__"} />
      </TableCell>
      <TableCell style={{ width: `${tabValue !== 0 && "20%"}` }}>
        {BusinessTypeName || "__"}
      </TableCell>
      <TableCell style={{ width: `${tabValue !== 0 && "20%"}` }}>
        {BusinessVerificationTypeName || "__"}
      </TableCell>
      {tabValue === 0 && (
        <TableCell>
          <Chip
            label={
              businessVerification.inprogressRequestStatus[
                BUSINESS_VERIFICATION_STATUS_LOCALE_KEYS[
                  RequestStatusId
                ]
              ]
            }
            className={`verification-request-chip text-break ${chipsStatusClass(RequestStatusId)}`}
          />
        </TableCell>
      )}
    </TableRow>
  );

  const renderTabContent = () => (
    <div>
      <div className="table-header my-4">
        <SearchInput
          name="request-search"
          id="review-request-search"
          className="review-request-search"
          value={searchValue}
          inputWrapperClass="search-input-wrapper search-input-wrapper-width"
          placeholder={businessVerification.list.searchPlaceholder}
          onChange={(value) => {
            setPage(1);
            setSearchValue(value);
          }}
        />
      </div>
      <div className="table-content nowrap-table">
        {requestsList?.length ? (
          <Table
            className={"px-0"}
            headlines={[
              businessVerification.list.companyName,
              businessVerification.list.requestDate,
              businessVerification.list.country,
              businessVerification.list.businessType,
              businessVerification.list.verificationMethod,
              tabValue === 0 ? businessVerification.list.status : ""
            ]}
            rows={
              !isLoading &&
              !!requestsList?.length &&
              requestsList.map((request) =>
                renderRequestTableRow(request)
              )
            }
          />
        ) : (
          <div>
            {!isLoading && (
              <EmptyState
                subTitle={
                  !!searchValue || !areAllParamsEmpty(paramsObject)
                    ? shared.noSearchResults
                    : businessVerification.list.noDataPlaceholder
                }
                className="empty-state-container"
                imgSrc={noImporterImg}
              />
            )}
          </div>
        )}
      </div>
      <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
        {!isLoading && requestsList?.length > 0 && (
          <Pagination
            count={requestsMeta?.pages}
            page={requestsMeta?.currentPage || page}
            handleChange={(page) => {
              setPage(page);
            }}
            defaultPage={1}
          />
        )}
      </div>
    </div>
  );

  const tabsArray = [
    {
      label: businessVerification.requestStatus.inProgress,
      content: renderTabContent()
    },
    {
      label: businessVerification.requestStatus.accepted,
      content: renderTabContent()
    },
    {
      label: businessVerification.requestStatus.rejected,
      content: renderTabContent()
    }
  ];

  return (
    <div className="verification-requests-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-center">
        <div className="title-count-container d-flex justify-content-center align-items-center">
          <Typography variant="h5" className="title-color me-3">
            {businessVerification.list.title}
          </Typography>
        </div>
        <div className="title-buttons-container">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={setIsApplyFilters}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
        </div>
      </div>
      <div className="products-table-container bg-white d-flex flex-column w-100  p-4 bg-white border-radius-8 mb-4">
        <div className="tabs-section">
          <Tabs
            tabs={tabsArray}
            value={tabValue}
            handleChange={setTabValue}
            activeTabAdornment={
              isLoading ? null : (
                <span className="records-count faint-blue-bg border-radius-50 p-2">
                  {requestsMeta?.count || 0}
                </span>
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

export default VerificationRequestsList;
